import { menuItems, type NavItem } from './navData'

/**
 * The footer is derived from `menuItems` so labels and URLs only ever live in
 * one place. It differs from the header in two ways we model explicitly:
 *
 * - grouping: the footer flattens the header's mega-menu columns into plain
 *   headed lists, and splits them across 4 columns (desktop) or 2 (mobile).
 * - labels: a handful of entries need more context when read out of the
 *   header's iconography (see `LABEL_OVERRIDES`).
 *
 * Anything genuinely footer-only (i.e. deliberately absent from the header)
 * is declared inline as an object literal rather than referenced by path.
 */

export type FooterLink = {
  label: string
  to: string
  external: boolean
  badge?: string
}

export type FooterSection = {
  key: string
  title: string
  links: FooterLink[]
}

/** Footer wording that intentionally differs from the header. */
const LABEL_OVERRIDES: Record<string, string> = {
  '/snowflake': 'Omni + Snowflake',
  '/databricks': 'Omni + Databricks',
  '/dbt': 'Omni + dbt',
}

const navItemsByPath = new Map<string, NavItem>()

const collectNavItems = (items: NavItem[]) => {
  for (const item of items) {
    if (item.to) navItemsByPath.set(item.to, item)
    if (item.children) collectNavItems(item.children)
  }
}

for (const menuItem of menuItems) {
  if (menuItem.to) {
    navItemsByPath.set(menuItem.to, {
      label: menuItem.title,
      to: menuItem.to,
    })
  }
  for (const column of menuItem.columns ?? []) collectNavItems(column.items)
}

/** Every path the footer pulls from the header, for the sync test. */
export const referencedNavPaths = new Set<string>()

const isExternal = (to: string) => /^https?:\/\//.test(to)

/**
 * Resolve a header nav entry by path. Returns `null` (rather than throwing)
 * when the path has disappeared from `menuItems` so a rename degrades to a
 * missing footer link instead of a blank site; `footerData.test.ts` fails the
 * build in that case.
 */
const fromNav = (to: string, badge?: string): FooterLink | null => {
  referencedNavPaths.add(to)
  const item = navItemsByPath.get(to)
  if (!item?.label) return null

  return {
    label: LABEL_OVERRIDES[to] ?? item.label,
    to,
    external: isExternal(to),
    badge,
  }
}

/** A link that deliberately exists only in the footer. */
const footerOnly = (label: string, to: string): FooterLink => ({
  label,
  to,
  external: isExternal(to),
})

const section = (
  key: string,
  title: string,
  links: (FooterLink | null)[],
): FooterSection => ({
  key,
  title,
  links: links.filter((link): link is FooterLink => link !== null),
})

const sections: FooterSection[] = [
  section('product', 'Product', [
    fromNav('/ai'),
    fromNav('/semantic-layer'),
    fromNav('/business-intelligence'),
    fromNav('/embedded-analytics'),
    fromNav('/integrations'),
  ]),
  section('capabilities', 'Capabilities', [
    fromNav('/apps'),
    fromNav('/spreadsheets'),
    fromNav('/calculations'),
    fromNav('/data-input'),
  ]),
  section('solutions', 'Solutions', [
    fromNav('/industry/media-advertising'),
    fromNav('/industry/saas'),
    fromNav('/industry/financial-services'),
    fromNav('/industry/retail'),
  ]),
  section('learn', 'Learn', [
    fromNav('/blog'),
    fromNav('/events'),
    fromNav('https://docs.omni.co'),
    fromNav('https://docs.omni.co/guides'),
    fromNav('/developers'),
    fromNav('https://community.omni.co/'),
    fromNav('https://docs.omni.co/changelog'),
    fromNav('https://docs.omni.co/demos'),
    fromNav('/security'),
    // Articles is footer-only by design — we don't want it in the main menu.
    footerOnly('Articles', '/articles'),
  ]),
  section('customers', 'Customers', [
    fromNav('/customer-case-studies'),
    fromNav('/customer-support'),
  ]),
  section('partners', 'Partners', [
    fromNav('/partners'),
    fromNav('/snowflake'),
    fromNav('/databricks'),
    fromNav('/dbt'),
  ]),
  section('compare', 'Compare', [
    fromNav('/omni-vs-tableau'),
    fromNav('/omni-vs-power-bi'),
    fromNav('/omni-vs-looker'),
    fromNav('/omni-vs-hex'),
    fromNav('/omni-vs-sigma'),
    fromNav('/omni-vs-thoughtspot'),
    fromNav('/omni-vs-metabase'),
    fromNav('/omni-vs-strategy'),
  ]),
  section('company', 'Company', [
    fromNav('/about'),
    fromNav('https://jobs.ashbyhq.com/omni', 'We’re hiring'),
    fromNav('/subscribe'),
    fromNav('/omnivision'),
    fromNav('/explo'),
  ]),
]

const sectionsByKey = new Map(sections.map((s) => [s.key, s]))

const toColumns = (keys: string[][]): FooterSection[][] =>
  keys.map((column) =>
    column
      .map((key) => sectionsByKey.get(key))
      .filter((s): s is FooterSection => s !== undefined),
  )

/** Grouped for balance, not for meaning — the sections carry the meaning. */
export const footerColumnsDesktop = toColumns([
  ['product', 'capabilities'],
  ['solutions', 'learn'],
  ['partners', 'compare'],
  ['company', 'customers'],
])

export const footerColumnsMobile = toColumns([
  ['product', 'capabilities', 'customers', 'partners', 'compare'],
  ['solutions', 'learn', 'company'],
])

export const footerSections = sections
